/**
 * WerkstattAnlegenModal — Issue #588
 * ------------------------------------
 * Modal to create a new Werkstatt-Akte from a Gutachten-Akte.
 * Pre-fills all fields from the Gutachten and shows FieldHints for each.
 * On submit: inserts the Werkstatt-Akte and cross-links both sides.
 */

import { useState } from 'react';
import { X, Wrench } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import FieldHint from '@/components/ui/FieldHint';
import {
  buildWerkstattPrefill,
  buildWerkstattAkteInsert,
  FIELD_HINTS,
} from '@/lib/werkstatt-akte-aus-gutachten';
import type { Akte } from '@/lib/database.types';

interface Props {
  open: boolean;
  onClose: () => void;
  gutachtenAkte: Akte;
  onSuccess?: () => void;
}

export default function WerkstattAnlegenModal({ open, onClose, gutachtenAkte, onSuccess }: Props) {
  const prefill = buildWerkstattPrefill(gutachtenAkte);

  const [kundeName, setKundeName] = useState(prefill.kundeName ?? '');
  const [fahrzeugMarke, setFahrzeugMarke] = useState(prefill.fahrzeugMarke ?? '');
  const [fahrzeugModell, setFahrzeugModell] = useState(prefill.fahrzeugModell ?? '');
  const [kennzeichen, setKennzeichen] = useState(prefill.kennzeichen ?? '');
  const [fin, setFin] = useState(prefill.fin ?? '');
  const [schadennummer, setSchadennummer] = useState(prefill.schadennummer ?? '');
  const [versicherungGegnerisch, setVersicherungGegnerisch] = useState(prefill.versicherungGegnerisch ?? '');
  const [unfalldatum, setUnfalldatum] = useState(prefill.unfalldatum ?? '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const insertPayload = buildWerkstattAkteInsert({
        ...prefill,
        kundeName: kundeName || null,
        fahrzeugMarke: fahrzeugMarke || null,
        fahrzeugModell: fahrzeugModell || null,
        kennzeichen: kennzeichen || null,
        fin: fin || null,
        schadennummer: schadennummer || null,
        versicherungGegnerisch: versicherungGegnerisch || null,
        unfalldatum: unfalldatum || null,
      });

      const { data: newAkte, error: insertErr } = await supabase
        .from('akten')
        .insert(insertPayload as any)
        .select()
        .single();

      if (insertErr) throw insertErr;

      // Cross-link the Gutachten-Akte back to the new Werkstatt-Akte
      const { error: updateErr } = await supabase
        .from('akten')
        .update({ werkstatt_akte_id: newAkte.id })
        .eq('id', gutachtenAkte.id);

      if (updateErr) throw updateErr;

      toast.success('Werkstatt-Akte angelegt', {
        description: `Werkstatt-Akte wurde erstellt und mit ${prefill.gutachtenAktenNummer ?? 'Gutachten'} verknüpft.`,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error('Fehler beim Anlegen', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={onClose}
      data-testid="werkstatt-anlegen-modal"
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-2xl shadow-2xl sm:max-w-lg sm:mx-4 sm:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
              <Wrench className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Werkstatt-Akte anlegen</h3>
              <p className="text-[10px] text-gray-400">
                Aus Gutachten {prefill.gutachtenAktenNummer ?? gutachtenAkte.id}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Kunde */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Kunde (Anspruchsteller)
              </label>
              <FieldHint text={FIELD_HINTS.kunde} />
            </div>
            <input
              type="text"
              data-testid="modal-kunde-name"
              value={kundeName}
              onChange={e => setKundeName(e.target.value)}
              placeholder="Name des Anspruchstellers"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
            />
          </div>

          {/* Fahrzeug Marke/Modell */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Fahrzeug (Marke / Modell)
              </label>
              <FieldHint text={FIELD_HINTS.fahrzeug} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={fahrzeugMarke}
                onChange={e => setFahrzeugMarke(e.target.value)}
                placeholder="Marke"
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
              />
              <input
                type="text"
                value={fahrzeugModell}
                onChange={e => setFahrzeugModell(e.target.value)}
                placeholder="Modell"
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
              />
            </div>
          </div>

          {/* Kennzeichen / FIN */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Kennzeichen / FIN
              </label>
              <FieldHint text={FIELD_HINTS.kennzeichenFin} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                data-testid="modal-kennzeichen"
                value={kennzeichen}
                onChange={e => setKennzeichen(e.target.value)}
                placeholder="MK-AB-123"
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
              />
              <input
                type="text"
                value={fin}
                onChange={e => setFin(e.target.value)}
                placeholder="FIN / VIN"
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
              />
            </div>
          </div>

          {/* Schadennummer */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Schadennummer
              </label>
              <FieldHint text={FIELD_HINTS.schadennummer} />
            </div>
            <input
              type="text"
              data-testid="modal-schadennummer"
              value={schadennummer}
              onChange={e => setSchadennummer(e.target.value)}
              placeholder="SN-2026-001"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
            />
          </div>

          {/* Versicherung gegnerisch */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Versicherung gegnerisch
              </label>
              <FieldHint text={FIELD_HINTS.versicherungGegnerisch} />
            </div>
            <input
              type="text"
              value={versicherungGegnerisch}
              onChange={e => setVersicherungGegnerisch(e.target.value)}
              placeholder="Name der Versicherung"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
            />
          </div>

          {/* Unfalldatum */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Unfalldatum
              </label>
              <FieldHint text={FIELD_HINTS.unfalldatum} />
            </div>
            <input
              type="date"
              value={unfalldatum}
              onChange={e => setUnfalldatum(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 transition-all"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="button"
            data-testid="modal-submit"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold rounded-xl bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Anlegen…' : 'Werkstatt-Akte anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
